<template>
  <div class="relatorio-csat-page">
    <div class="dashboard-header mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="dashboard-header-titulo-row">
          <h2 class="dashboard-title">
            <feather-icon
              icon="AwardIcon"
              size="32"
              class="titulo-icon-left"
            />
            <span class="titulo-texto">Relatório CSAT</span>
            <router-link
              :to="{ name: 'dashboard-main' }"
              class="dashboard-header-home"
              title="Ir para o início"
            >
              <feather-icon
                icon="HomeIcon"
                size="16"
                class="dashboard-header-home-icon"
              />
            </router-link>
          </h2>
        </div>
        <p class="text-muted mb-0 w-100 dashboard-header-subtitle">
          Satisfação dos clientes: total de respostas, pontuação e distribuição por nota (1 a 5)
        </p>
      </div>
    </div>

    <b-card
      v-if="facebookAccountsLista.length > 1"
      class="mb-4 card-selecao-loja"
    >
      <div class="d-flex flex-wrap align-items-center">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2 text-primary"
        />
        <label class="mb-0 mr-3 font-weight-bold">Selecionar Loja:</label>
        <b-form-select
          v-model="chatSelecionado"
          :options="facebookAccountsLista.map(conta => ({
            value: conta.chat_token,
            text: conta.name
          }))"
          class="select-loja"
          @change="carregarDados"
        />
      </div>
    </b-card>

    <b-card class="filtros-card periodo-card mb-4">
      <div
        class="periodo-card-trigger d-flex align-items-center justify-content-between"
        @click="periodoCardAberto = !periodoCardAberto"
      >
        <div class="d-flex align-items-center flex-wrap">
          <feather-icon icon="CalendarIcon" size="20" class="periodo-trigger-icon text-primary mr-2" />
          <span class="periodo-trigger-label">{{ periodoTriggerLabel }}</span>
          <span class="periodo-trigger-range text-muted ml-2">{{ periodoTriggerRange }}</span>
        </div>
        <feather-icon
          :icon="periodoCardAberto ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          size="20"
          class="periodo-trigger-chevron text-muted"
        />
      </div>
      <b-collapse v-model="periodoCardAberto" class="mt-3">
        <div class="periodo-card-body">
          <div class="row">
            <div class="col-12 col-md-4 periodo-presets">
              <h6 class="periodo-presets-title">Intervalo de data</h6>
              <div class="periodo-presets-list">
                <button
                  type="button"
                  class="periodo-preset-btn"
                  :class="{ active: atalhoDias === 7 }"
                  @click.stop="aplicarPeriodo(7)"
                >
                  Últimos 7 dias
                </button>
                <button
                  type="button"
                  class="periodo-preset-btn"
                  :class="{ active: atalhoDias === 15 }"
                  @click.stop="aplicarPeriodo(15)"
                >
                  Últimos 15 dias
                </button>
                <button
                  type="button"
                  class="periodo-preset-btn"
                  :class="{ active: atalhoDias === 31 }"
                  @click.stop="aplicarPeriodo(31)"
                >
                  Últimos 31 dias
                </button>
                <button
                  type="button"
                  class="periodo-preset-btn"
                  :class="{ active: atalhoDias === null }"
                  @click.stop="selecionarPersonalizado"
                >
                  Personalizado
                </button>
              </div>
            </div>
            <div class="col-12 col-md-8">
              <div class="row">
                <div class="col-12 col-sm-6">
                  <label class="periodo-field-label">Data de início</label>
                  <b-form-datepicker
                    v-model="periodoInicio"
                    locale="pt-BR"
                    :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
                    class="periodo-datepicker w-100"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="periodo-field-label">Data final</label>
                  <b-form-datepicker
                    v-model="periodoFim"
                    locale="pt-BR"
                    :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
                    class="periodo-datepicker w-100"
                  />
                </div>
              </div>
              <div class="periodo-card-footer d-flex justify-content-end gap-2 mt-3">
                <b-button variant="outline-secondary" size="sm" @click.stop="limparPeriodo">
                  Limpar
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="carregando"
                  @click.stop="aplicarPeriodoEBuscar"
                >
                  <b-spinner v-if="carregando" small type="grow" class="mr-1" />
                  Aplicar
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
      <p class="text-muted small mb-0 mt-2">
        Período máximo: 31 dias. Padrão: últimos 7 dias.
      </p>
    </b-card>

    <!-- Métricas principais -->
    <div class="row mb-4">
      <div class="col-12 col-md-4 mb-3 mb-md-0">
        <b-card class="csat-metric-card h-100">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="csat-metric-label">Total de respostas</span>
            <b-badge
              v-b-tooltip.hover
              variant="light-secondary"
              class="csat-metric-info"
              title="Quantidade de conversas que responderam à pesquisa de satisfação (CSAT)."
            >
              <feather-icon icon="InfoIcon" size="14" />
            </b-badge>
          </div>
          <div class="csat-metric-value">
            {{ totalRespostas }}
          </div>
        </b-card>
      </div>
      <div class="col-12 col-md-4 mb-3 mb-md-0">
        <b-card class="csat-metric-card h-100">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="csat-metric-label">Pontuação de satisfação</span>
            <b-badge
              v-b-tooltip.hover
              variant="light-secondary"
              class="csat-metric-info"
              title="(Respostas positivas — notas 4 e 5 — ÷ total de respostas) × 100"
            >
              <feather-icon icon="InfoIcon" size="14" />
            </b-badge>
          </div>
          <div class="csat-metric-value">
            {{ pontuacaoSatisfacaoTexto }}%
          </div>
        </b-card>
      </div>
      <div class="col-12 col-md-4">
        <b-card class="csat-metric-card h-100">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="csat-metric-label">Taxa de resposta</span>
            <b-badge
              v-b-tooltip.hover
              variant="light-secondary"
              class="csat-metric-info"
              title="(Total de respostas ÷ total de conversas que receberam CSAT no período) × 100"
            >
              <feather-icon icon="InfoIcon" size="14" />
            </b-badge>
          </div>
          <div class="csat-metric-value">
            {{ taxaRespostasTexto }}%
          </div>
        </b-card>
      </div>
    </div>

    <!-- Distribuição por nota -->
    <b-card class="rating-distribution-card">
      <h5 class="card-title-custom mb-3">
        <feather-icon icon="BarChart2Icon" size="20" class="mr-2" />
        Distribuição de avaliações
      </h5>
      <p class="text-muted small mb-3">
        Notas de 1 (ruim) a 5 (excelente). Quantidade e percentual por nota.
      </p>

      <!-- Barra visual da distribuição -->
      <div
        v-if="totalRespostas > 0"
        class="rating-bar-container mb-4"
      >
        <div class="rating-bar d-flex">
          <div
            v-for="nota in [5, 4, 3, 2, 1]"
            :key="nota"
            class="rating-bar-segment"
            :style="{
              width: percentualPorNota(nota) + '%',
              backgroundColor: corPorNota(nota),
            }"
            :title="`Nota ${nota}: ${csatPorNota[nota] || 0} (${percentualPorNota(nota).toFixed(1)}%)`"
          />
        </div>
      </div>
      <div
        v-else
        class="rating-bar-container mb-4"
      >
        <div class="rating-bar rating-bar-empty" />
      </div>

      <!-- Lista por nota: 5 → 1 -->
      <div class="rating-list">
        <div
          v-for="item in ratingLabels"
          :key="item.nota"
          class="rating-list-item d-flex align-items-center justify-content-between py-2 border-bottom"
        >
          <span class="rating-list-label">
            <span class="rating-emoji">{{ item.emoji }}</span>
            <span class="rating-nome">{{ item.nome }}</span>
          </span>
          <span class="rating-list-value">
            {{ percentualPorNota(item.nota).toFixed(1) }}% ({{ csatPorNota[item.nota] || 0 }})
          </span>
        </div>
      </div>
    </b-card>

    <!-- CSAT por atendente -->
    <b-card class="csat-atendentes-card mb-4">
      <h5 class="card-title-custom mb-3">
        <feather-icon icon="UsersIcon" size="20" class="mr-2" />
        CSAT por atendente
      </h5>
      <p class="text-muted small mb-3">
        Média de satisfação e quantidade de respostas por atendente no período.
      </p>
      <div class="csat-atendentes-list">
        <div
          v-for="(media, nome) in csatMediaPorAtendente"
          :key="nome"
          class="csat-atendente-item d-flex align-items-center justify-content-between py-2 px-3 border rounded mb-2"
        >
          <span class="font-weight-medium">{{ nome }}</span>
          <span class="csat-atendente-media">
            {{ media.toFixed(2) }}
            <span class="text-muted small ml-1">({{ csatContagemPorAtendente[nome] || 0 }} respostas)</span>
          </span>
        </div>
        <p
          v-if="Object.keys(csatMediaPorAtendente).length === 0"
          class="text-muted mb-0"
        >
          Nenhum atendente com respostas CSAT no período.
        </p>
      </div>
    </b-card>

    <!-- Tabela de atendimentos -->
    <b-card class="table-card">
      <h5 class="card-title-custom mb-3">
        <feather-icon icon="ListIcon" size="20" class="mr-2" />
        Atendimentos e CSAT
      </h5>
      <b-card class="filtros-lista-card mb-3">
        <div class="d-flex flex-wrap align-items-end gap-2">
          <div>
            <label class="mb-1 text-muted small">Atendente</label>
            <b-form-select
              v-model="filtroAtendente"
              :options="opcoesAtendentes"
              size="sm"
              class="filtro-select"
            />
          </div>
          <div>
            <label class="mb-1 text-muted small">Nota CSAT</label>
            <b-form-select
              v-model="filtroCsat"
              :options="opcoesCsat"
              size="sm"
              class="filtro-select"
            />
          </div>
        </div>
      </b-card>
      <b-table
        :items="tabelaAtendimentosPagina"
        :fields="camposTabela"
        striped
        hover
        responsive
        bordered
        show-empty
        empty-text="Nenhum atendimento com nota CSAT e atendente identificado no período."
        class="tabela-atendimentos-csat"
        @row-clicked="abrirHistorico"
      >
        <template #cell(cliente)="data">
          {{ data.item.cliente_nome || '—' }}
        </template>
        <template #cell(whatsapp)="data">
          {{ data.item.cliente_whatsapp || '—' }}
        </template>
        <template #cell(atendente_nome)="data">
          {{ data.item.atendente_nome || '—' }}
        </template>
        <template #cell(csat_score)="data">
          <span
            :class="{
              'text-danger font-weight-bold': data.item.csat_score != null && data.item.csat_score <= 2,
              'text-warning font-weight-bold': data.item.csat_score === 3,
              'text-success font-weight-bold': data.item.csat_score != null && data.item.csat_score >= 4
            }"
          >
            {{ data.item.csat_score != null ? data.item.csat_score : '—' }}
          </span>
        </template>
        <template #cell(inicio)="data">
          {{ formatarData(data.item.inicio) }}
        </template>
      </b-table>
      <div
        v-if="atendimentosFiltrados.length > perPage"
        class="d-flex justify-content-between align-items-center flex-wrap mt-2 px-1"
      >
        <span class="text-muted small">
          Mostrando {{ (paginaAtual - 1) * perPage + 1 }} a
          {{ Math.min(paginaAtual * perPage, atendimentosFiltrados.length) }}
          de {{ atendimentosFiltrados.length }} atendimentos
        </span>
        <b-pagination
          v-model="paginaAtual"
          :total-rows="atendimentosFiltrados.length"
          :per-page="perPage"
          size="sm"
          align="right"
          pills
        />
      </div>
    </b-card>

    <ModalHistoricoAtendimento
      :visivel.sync="modalHistoricoVisivel"
      :historico="atendimentoSelecionado"
      :atendentes="listaAtendentesParaModal"
    />
  </div>
</template>

<script>
import {
  buscaChatsTeste,
  buscaResolvidos,
  buscaAtendentes,
  getChatOfContact,
} from '@/functions/basics/chat'
import ModalHistoricoAtendimento from './ModalHistoricoAtendimento'

export default {
  name: 'RelatorioCsat',
  components: { ModalHistoricoAtendimento },
  data() {
    const fim = new Date()
    const inicio = new Date()
    inicio.setDate(inicio.getDate() - 6)
    return {
      facebookAccountsLista: [],
      chatSelecionado: null,
      carregando: false,
      periodoInicio: inicio,
      periodoFim: fim,
      atalhoDias: 7,
      periodoCardAberto: false,
      resolvidos: [],
      mapaAtendentes: {},
      atendimentos: [],
      csatMediaPorAtendente: {},
      csatContagemPorAtendente: {},
      filtroAtendente: null,
      filtroCsat: null,
      perPage: 15,
      paginaAtual: 1,
      modalHistoricoVisivel: false,
      atendimentoSelecionado: null,
      camposTabela: [
        { key: 'cliente', label: 'Cliente' },
        { key: 'whatsapp', label: 'WhatsApp' },
        { key: 'atendente_nome', label: 'Atendente' },
        { key: 'csat_score', label: 'CSAT' },
        { key: 'inicio', label: 'Início' },
      ],
      // Derivados após carregar
      totalRespostas: 0,
      totalResolvidos: 0,
      respostasPositivas: 0,
      pontuacaoSatisfacao: 0,
      taxaRespostas: 0,
      csatPorNota: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      ratingLabels: [
        { nota: 5, nome: 'Excelente', emoji: '🤩' },
        { nota: 4, nome: 'Bom', emoji: '😀' },
        { nota: 3, nome: 'Mediano', emoji: '🙂' },
        { nota: 2, nome: 'Neutro', emoji: '😐' },
        { nota: 1, nome: 'Ruim', emoji: '😞' },
      ],
    }
  },
  computed: {
    periodoTriggerLabel() {
      if (this.atalhoDias === 7) return 'Últimos 7 dias'
      if (this.atalhoDias === 15) return 'Últimos 15 dias'
      if (this.atalhoDias === 31) return 'Últimos 31 dias'
      return 'Período personalizado'
    },
    periodoTriggerRange() {
      const i = this.periodoInicio ? new Date(this.periodoInicio) : null
      const f = this.periodoFim ? new Date(this.periodoFim) : null
      if (!i || !f) return ''
      const fmt = d => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(i)} - ${fmt(f)}`
    },
    listaAtendentesParaModal() {
      return Object.entries(this.mapaAtendentes || {}).map(([id, name]) => ({
        id: parseInt(id, 10),
        value: parseInt(id, 10),
        name,
        text: name
      }))
    },
    pontuacaoSatisfacaoTexto() {
      return this.pontuacaoSatisfacao.toFixed(1)
    },
    taxaRespostasTexto() {
      return this.taxaRespostas.toFixed(1)
    },
    /** Atendimentos que entram na tabela: somente com nota CSAT (1-5) e atendente diferente de Desconhecido */
    atendimentosComCsat() {
      return this.atendimentos.filter(at => {
        const temCsat = at.csat_score >= 1 && at.csat_score <= 5
        const atendenteOk = at.atendente_nome && at.atendente_nome !== 'Desconhecido'
        return temCsat && atendenteOk
      })
    },
    opcoesAtendentes() {
      const nomes = [...new Set(this.atendimentosComCsat.map(a => a.atendente_nome).filter(Boolean))]
      nomes.sort((a, b) => (a === 'Bot' ? -1 : a.localeCompare(b)))
      return [
        { value: null, text: 'Todos' },
        ...nomes.map(n => ({ value: n, text: n })),
      ]
    },
    opcoesCsat() {
      return [
        { value: null, text: 'Todas' },
        { value: 1, text: '1' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5' },
      ]
    },
    atendimentosFiltrados() {
      return this.atendimentosComCsat.filter(at => {
        const okAtendente = !this.filtroAtendente || at.atendente_nome === this.filtroAtendente
        const okCsat = !this.filtroCsat || at.csat_score === this.filtroCsat
        return okAtendente && okCsat
      })
    },
    tabelaAtendimentosPagina() {
      const start = (this.paginaAtual - 1) * this.perPage
      return this.atendimentosFiltrados.slice(start, start + this.perPage)
    },
  },
  watch: {
    filtroAtendente() {
      this.paginaAtual = 1
    },
    filtroCsat() {
      this.paginaAtual = 1
    },
  },
  watch: {
    periodoInicio() {
      this.atualizarAtalhoDiasPelasDatas()
    },
    periodoFim() {
      this.atualizarAtalhoDiasPelasDatas()
    },
  },
  mounted() {
    this.carregarLojas().then(() => {
      this.carregarDados()
    })
  },
  methods: {
    aplicarPeriodo(dias) {
      const fim = new Date()
      const inicio = new Date()
      inicio.setDate(fim.getDate() - dias + 1)
      this.periodoInicio = inicio
      this.periodoFim = fim
      this.atalhoDias = dias
    },
    limparPeriodo() {
      this.aplicarPeriodo(7)
    },
    selecionarPersonalizado() {
      this.atalhoDias = null
    },
    atualizarAtalhoDiasPelasDatas() {
      const i = this.periodoInicio ? new Date(this.periodoInicio) : null
      const f = this.periodoFim ? new Date(this.periodoFim) : null
      if (!i || !f || Number.isNaN(i.getTime()) || Number.isNaN(f.getTime())) return
      const diffDias = Math.round((f - i) / (1000 * 60 * 60 * 24)) + 1
      if (diffDias === 7) this.atalhoDias = 7
      else if (diffDias === 15) this.atalhoDias = 15
      else if (diffDias === 31) this.atalhoDias = 31
      else this.atalhoDias = null
    },
    aplicarPeriodoEBuscar() {
      this.periodoCardAberto = false
      this.carregarDados()
    },
    percentualPorNota(nota) {
      if (this.totalRespostas <= 0) return 0
      const n = this.csatPorNota[nota] || 0
      return (n / this.totalRespostas) * 100
    },
    corPorNota(nota) {
      const cores = { 1: '#ea5455', 2: '#ff9f43', 3: '#ffc107', 4: '#28c76f', 5: '#28c76f' }
      return cores[nota] || '#b8c2cc'
    },
    async carregarLojas() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []
      if ((contas.chats || []).length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if ((contas.chats || []).length > 1) {
        this.chatSelecionado = (contas.chats[0] && contas.chats[0].chat_token) || null
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async carregarDados() {
      if (!this.chatSelecionado) return
      const inicio = new Date(this.periodoInicio)
      const fim = new Date(this.periodoFim)
      if (inicio > fim) {
        this.$bvToast.toast('A data de início não pode ser maior que a data de fim.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }
      const diffDias = Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24))
      if (diffDias > 31) {
        this.$bvToast.toast('O período não pode exceder 31 dias.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.carregando = true
      const uuid = this.chatSelecionado
      const inicioISO = inicio.toISOString().split('T')[0]
      const fimISO = fim.toISOString().split('T')[0]

      try {
        const [dadosResolvidos, dadosAtendentes] = await Promise.all([
          buscaResolvidos(uuid, inicioISO, fimISO, 'all'),
          buscaAtendentes(uuid),
        ])
        const lista = dadosResolvidos?.resolvidos || []
        this.resolvidos = lista

        const mapaAtendentes = {}
        ;(dadosAtendentes?.atendentes || []).forEach(a => {
          mapaAtendentes[a.id] = a.name
        })
        this.mapaAtendentes = mapaAtendentes

        const csatMediaPorAtendente = {}
        const csatContagemPorAtendente = {}
        const atendimentos = lista.map(a => {
          const atendenteNome = a.atendimento_bot
            ? 'Bot'
            : (a.atendente_ativo?.id != null ? mapaAtendentes[a.atendente_ativo.id] : null) || 'Desconhecido'
          if (a.csat_score >= 1 && a.csat_score <= 5) {
            csatMediaPorAtendente[atendenteNome] = (csatMediaPorAtendente[atendenteNome] || 0) + a.csat_score
            csatContagemPorAtendente[atendenteNome] = (csatContagemPorAtendente[atendenteNome] || 0) + 1
          }
          return {
            ...a,
            atendente_nome: atendenteNome,
            cliente_nome: a.cliente?.name || a.contato?.name || null,
            cliente_whatsapp: a.cliente?.whatsapp || a.contato?.phone || a.from || null,
            atendimento_id: a.id || a.atendimento_id || a.ticket_id,
          }
        })
        Object.keys(csatMediaPorAtendente).forEach(nome => {
          csatMediaPorAtendente[nome] = csatMediaPorAtendente[nome] / csatContagemPorAtendente[nome]
        })
        this.atendimentos = atendimentos
        this.csatMediaPorAtendente = csatMediaPorAtendente
        this.csatContagemPorAtendente = csatContagemPorAtendente
        this.paginaAtual = 1

        const totalResolvidos = lista.length
        const comNota = lista.filter(r => r.csat_score >= 1 && r.csat_score <= 5)
        const totalRespostas = comNota.length
        const respostasPositivas = comNota.filter(r => r.csat_score === 4 || r.csat_score === 5).length

        this.totalResolvidos = totalResolvidos
        this.totalRespostas = totalRespostas
        this.respostasPositivas = respostasPositivas
        this.pontuacaoSatisfacao = totalRespostas > 0
          ? (respostasPositivas / totalRespostas) * 100
          : 0
        this.taxaRespostas = totalResolvidos > 0
          ? (totalRespostas / totalResolvidos) * 100
          : 0

        const porNota = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        comNota.forEach(r => {
          const n = Number(r.csat_score)
          if (n >= 1 && n <= 5) porNota[n] = (porNota[n] || 0) + 1
        })
        this.csatPorNota = porNota
      } catch (err) {
        console.error('Erro ao carregar relatório CSAT:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar dados.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.totalRespostas = 0
        this.totalResolvidos = 0
        this.respostasPositivas = 0
        this.pontuacaoSatisfacao = 0
        this.taxaRespostas = 0
        this.csatPorNota = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        this.atendimentos = []
        this.csatMediaPorAtendente = {}
        this.csatContagemPorAtendente = {}
      } finally {
        this.carregando = false
      }
    },
    formatarData(date) {
      if (!date) return '—'
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    async abrirHistorico(item) {
      const id = item.atendimento_id || item.id
      if (!id) return
      try {
        const dados = await getChatOfContact(id)
        this.atendimentoSelecionado = {
          mensagens: dados.mensagens || dados,
          historicoAtendentes: dados.historico_atendentes || [],
          status: dados.status || 'resolvido',
          fim: dados.fim || null,
        }
        this.modalHistoricoVisivel = true
      } catch (err) {
        console.error('Erro ao carregar histórico:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar histórico do atendimento.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style>
body:has(.relatorio-csat-page) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100% !important;
}
body:has(.relatorio-csat-page) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}
body:has(.relatorio-csat-page) .content-wrapper,
body:has(.relatorio-csat-page) .content-body {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}
body:has(.relatorio-csat-page) .content-right {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
</style>

<style scoped>
.relatorio-csat-page {
  padding: 1rem;
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
}

.dashboard-header {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.25);
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.dashboard-title .titulo-icon-left { flex-shrink: 0; }
.dashboard-title .titulo-texto { line-height: 1; }

.dashboard-header-home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);
  padding: 0.2rem;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s;
  flex-shrink: 0;
}

.dashboard-header-home >>> svg { display: block; width: 16px; height: 16px; }
.dashboard-header-home:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
}

.dashboard-header .text-muted { color: rgba(255, 255, 255, 0.9) !important; }
.dashboard-header-subtitle { margin-top: 0.15rem; }
.dashboard-header-titulo-row { margin-bottom: 0.15rem; }

.card-selecao-loja .select-loja { max-width: 300px; min-width: 0; }

.filtros-card.periodo-card,
.csat-metric-card,
.rating-distribution-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.periodo-card .card-body { padding: 1rem 1.25rem; }
.periodo-card-trigger { cursor: pointer; padding: 0.25rem 0; user-select: none; }
.periodo-trigger-icon { flex-shrink: 0; }
.periodo-trigger-label { font-weight: 600; color: #5e5873; }
.periodo-trigger-range { font-size: 0.9rem; }
.periodo-trigger-chevron { flex-shrink: 0; }
.periodo-card-body { border-top: 1px solid #ebe9f1; padding-top: 1rem; }
.periodo-presets-title { font-weight: 600; color: #5e5873; margin-bottom: 0.75rem; font-size: 0.9rem; }
.periodo-presets-list { display: flex; flex-direction: column; gap: 0.35rem; }
.periodo-preset-btn {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  background: #fff;
  color: #5e5873;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.periodo-preset-btn:hover { background: #f8f9fa; border-color: #9A3092; color: #9A3092; }
.periodo-preset-btn.active { background: #9A3092; border-color: #9A3092; color: #fff; }
.periodo-field-label { display: block; font-size: 0.85rem; font-weight: 500; color: #5e5873; margin-bottom: 0.35rem; }
.periodo-datepicker >>> .form-control { border-radius: 8px; border-color: #d8d6de; }
.periodo-card-footer { flex-wrap: wrap; }

.csat-metric-card .card-body { padding: 1rem 1.25rem; }
.csat-metric-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e5873;
}
.csat-metric-info { cursor: help; padding: 0.25rem 0.4rem; }
.csat-metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #5e5873;
}

.card-title-custom { font-weight: 600; color: #5e5873; }

.rating-bar-container { width: 100%; }
.rating-bar {
  height: 24px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  min-width: 100%;
}
.rating-bar-segment {
  min-width: 2px;
  transition: width 0.3s ease;
}
.rating-bar-empty {
  background: #ebe9f1;
}

.rating-list-item:last-child { border-bottom: none !important; }
.rating-list-label { display: flex; align-items: center; gap: 0.5rem; }
.rating-emoji { font-size: 1.25rem; }
.rating-nome { font-weight: 500; color: #5e5873; }
.rating-list-value { font-weight: 600; color: #5e5873; }

.csat-atendentes-card .card-body { padding: 1rem 1.25rem; }
.csat-atendente-item { background: #f8f9fa; }
.csat-atendente-media { font-weight: 600; color: #5e5873; }

.table-card .card-body { padding: 1rem 1.25rem; }
.filtro-select { min-width: 140px; }
.tabela-atendimentos-csat >>> tbody tr { cursor: pointer; }
</style>
